import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
    Box,
    Chip,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import { OrderType, OrderTypeLabel } from "../../directus/collections";
import { millisecondsToTimeComponents, toMilliseconds } from "../../libs/utils";
import { MatchFilter } from "../../libs/statsFilters";

interface FilterFormProps {
  filters: MatchFilter;
  onOrderTypeChange: (orderTypes: OrderType[]) => void;
  onDurationChange: (duration: {
    min?: number | null;
    max?: number | null;
  }) => void;
  onDateRangeChange: (dateRange: {
    startDate?: number | null;
    endDate?: number | null;
  }) => void;
  onEmailsChange: (emails: { exclude?: string[] }) => void;
  onChallengeDurationChange: (duration: {
    min?: number | null;
    max?: number | null;
  }) => void;
  onStateChange: (state: "created" | "started" | "fulfilled" | "completed") => void;
  onProgressChange: (progress: { minChallenges?: number }) => void;
}

/**
 * Componente per gestire gli Order Types
 */
const OrderTypeSection: React.FC<{
  orderTypes: OrderType[];
  onChange: (orderTypes: OrderType[]) => void;
}> = ({ orderTypes, onChange }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Tipi di Ordine
      </Typography>
      <FormControl fullWidth>
        <InputLabel>Seleziona tipi di ordine</InputLabel>
        <Select
          multiple
          value={orderTypes || []}
          onChange={(e) => onChange(e.target.value as OrderType[])}
          input={<OutlinedInput label="Seleziona tipi di ordine" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={OrderTypeLabel[value]} size="small" />
              ))}
            </Box>
          )}
        >
          {Object.entries(OrderTypeLabel).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const DurationField: React.FC<{
  label: string;
  value: number | null | undefined;
  onChange: (value: number | null | undefined) => void;
}> = ({ label, value, onChange }) => {
  const timeParts = millisecondsToTimeComponents(value || 0);

  const onPartChange = (parts: {
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
  }) => {
    onChange(toMilliseconds({ ...timeParts, ...parts }));
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      <Typography sx={{ minWidth: 100 }}>{label}:</Typography>
      <TextField
        label="Giorni"
        type="number"
        value={timeParts.days}
        slotProps={{ htmlInput: { min: 0 } }}
        onChange={(e) => {
          onPartChange({ days: Number(e.target.value) });
        }}
        size="small"
        sx={{ width: 80 }}
      />
      <TextField
        label="Ore"
        type="number"
        value={timeParts.hours}
        slotProps={{ htmlInput: { min: 0, max: 23 } }}
        onChange={(e) => {
          onPartChange({ hours: Number(e.target.value) });
        }}
        size="small"
        sx={{ width: 80 }}
      />
      <TextField
        label="Minuti"
        type="number"
        value={timeParts.minutes}
        slotProps={{ htmlInput: { min: 0, max: 59 } }}
        onChange={(e) => {
          onPartChange({ minutes: Number(e.target.value) });
        }}
        size="small"
        sx={{ width: 80 }}
      />
      <TextField
        label="Secondi"
        type="number"
        value={timeParts.seconds}
        slotProps={{ htmlInput: { min: 0, max: 59 } }}
        onChange={(e) => {
          onPartChange({ seconds: Number(e.target.value) });
        }}
        size="small"
        sx={{ width: 80 }}
      />
    </Stack>
  );
};

/**
 * Componente per gestire la durata
 */
const DurationSection: React.FC<{
  duration: { min?: number | null; max?: number | null };
  onChange: (duration: { min?: number | null; max?: number | null }) => void;
  title: string;
}> = ({ duration, onChange, title }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Stack direction="column" spacing={2}>
        <DurationField
          label="Min"
          value={duration.min}
          onChange={(value) => onChange({ ...duration, min: value })}
        />
        <DurationField
          label="Max"
          value={duration.max}
          onChange={(value) => onChange({ ...duration, max: value })}
        />
      </Stack>
    </Box>
  );
};

/**
 * Componente per gestire il range di date
 */
const DateRangeSection: React.FC<{
  dateRange: { startDate?: number | null; endDate?: number | null };
  onChange: (dateRange: {
    startDate?: number | null;
    endDate?: number | null;
  }) => void;
}> = ({ dateRange, onChange }) => {
  const formatDateForInput = (timestamp: number | null | undefined) => {
    if (!timestamp) return "";
    return new Date(timestamp).toISOString().split("T")[0];
  };

  const handleStartDateChange = (value: string) => {
    const timestamp = value ? new Date(value).getTime() : null;
    onChange({ ...dateRange, startDate: timestamp });
  };

  const handleEndDateChange = (value: string) => {
    const timestamp = value ? new Date(value).getTime() : null;
    onChange({ ...dateRange, endDate: timestamp });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Intervallo Date
      </Typography>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Data inizio"
          type="date"
          value={formatDateForInput(dateRange.startDate)}
          onChange={(e) => handleStartDateChange(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Data fine"
          type="date"
          value={formatDateForInput(dateRange.endDate)}
          onChange={(e) => handleEndDateChange(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
    </Box>
  );
};

/**
 * Componente per gestire le email escluse
 */
const EmailsSection: React.FC<{
  emails: { exclude?: string[] };
  onChange: (emails: { exclude?: string[] }) => void;
}> = ({ emails, onChange }) => {
  const [newEmail, setNewEmail] = useState("");

  const addEmail = () => {
    if (newEmail.trim() && !emails.exclude?.includes(newEmail.trim())) {
      const updatedEmails = [...(emails.exclude || []), newEmail.trim()];
      onChange({ exclude: updatedEmails });
      setNewEmail("");
    }
  };

  const removeEmail = (emailToRemove: string) => {
    const updatedEmails =
      emails.exclude?.filter((email) => email !== emailToRemove) || [];
    onChange({ exclude: updatedEmails.length > 0 ? updatedEmails : undefined });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Email da Escludere
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          label="Aggiungi email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && addEmail()}
          size="small"
          fullWidth
        />
        <IconButton onClick={addEmail} color="primary">
          <AddIcon />
        </IconButton>
      </Stack>
      {emails.exclude && emails.exclude.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {emails.exclude.map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={() => removeEmail(email)}
              deleteIcon={<DeleteIcon />}
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * Componente per gestire lo stato del match
 */
const StateSection: React.FC<{
  state?: "created" | "started" | "fulfilled" | "completed";
  onChange: (state: "created" | "started" | "fulfilled" | "completed") => void;
}> = ({ state, onChange }) => {
  const stateLabels = {
    created: "Creato",
    started: "Iniziato", 
    fulfilled: "Soddisfatto",
    completed: "Completato"
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Stato Match
      </Typography>
      <FormControl fullWidth>
        <InputLabel>Seleziona stato</InputLabel>
        <Select
          value={state}
          onChange={(e) => onChange(e.target.value as "created" | "started" | "fulfilled" | "completed")}
          input={<OutlinedInput label="Seleziona stato" />}
        >
          {Object.entries(stateLabels).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

/**
 * Componente per gestire i progressi
 */
const ProgressSection: React.FC<{
  progress: { minChallenges?: number };
  onChange: (progress: { minChallenges?: number }) => void;
}> = ({ progress, onChange }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Progressi
      </Typography>
      <TextField
        label="Numero minimo di sfide completate"
        type="number"
        value={progress.minChallenges || 0}
        onChange={(e) => onChange({ minChallenges: Number(e.target.value) })}
        slotProps={{ htmlInput: { min: 0 } }}
        size="small"
        fullWidth
      />
    </Box>
  );
};

/**
 * Form principale per modificare i filtri - Componente controllato
 */
export const FilterForm: React.FC<FilterFormProps> = ({
  filters,
  onOrderTypeChange,
  onDurationChange,
  onDateRangeChange,
  onEmailsChange,
  onChallengeDurationChange,
  onStateChange,
  onProgressChange,
}) => {
  return (
    <Box sx={{ p: 2 }}>
      <OrderTypeSection
        orderTypes={filters.orderType || []}
        onChange={onOrderTypeChange}
      />

      <Divider sx={{ my: 2 }} />

      <StateSection
        state={filters.state}
        onChange={onStateChange}
      />

      <Divider sx={{ my: 2 }} />

      <DurationSection
        title="Durata Match"
        duration={filters.duration || {}}
        onChange={onDurationChange}
      />

      <Divider sx={{ my: 2 }} />

      <DateRangeSection
        dateRange={filters.dateRange || {}}
        onChange={onDateRangeChange}
      />

      <Divider sx={{ my: 2 }} />

      <EmailsSection emails={filters.emails || {}} onChange={onEmailsChange} />

      <Divider sx={{ my: 2 }} />

      <ProgressSection
        progress={filters.progress || {}}
        onChange={onProgressChange}
      />

      <Divider sx={{ my: 2 }} />

      <DurationSection
        title="Durata Enigma"
        duration={filters.challenge?.duration || {}}
        onChange={onChallengeDurationChange}
      />
    </Box>
  );
};

export default FilterForm;
